import { HtmlHTMLAttributes } from 'react';

import { HTMLDataTrackerProps } from '@/app/types';
import { cn } from '@/lib/utils';

export const headingLevels = [
  'xl',
  'l',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
] as const;
export interface HeadingProps {
  level: (typeof headingLevels)[number];
  classNames?: {
    root?: string;
  };
}

type HeadingWithHtmlProps = HeadingProps &
  HTMLDataTrackerProps &
  HtmlHTMLAttributes<HTMLHeadingElement> & {
    children: React.ReactNode;
  };

const HeadingStyles = {
  xl: 'md:text-xl sm:text-h1 text-h2',
  l: 'md:text-l sm:text-h1 text-h2',
  h1: 'sm:text-h1 text-h2',
  h2: 'sm:text-h2 text-h3',
  h3: 'sm:text-h3 text-subtitle-l',
  h4: 'sm:text-h3 text-subtitle-l',
  h5: 'sm:text-h3 text-subtitle-l',
  h6: 'sm:text-h3 text-subtitle-l',
} as const;

export const getHeadingElement = (level: HeadingProps['level']) => {
  return (['xl', 'l'].includes(level) ? 'h1' : level) as
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6';
};

export default function Heading({
  children,
  level = 'h1',
  classNames,
  ...rest
}: HeadingWithHtmlProps) {
  const HeadingEl = getHeadingElement(level);
  const headingClass = HeadingStyles[level];

  return (
    <HeadingEl
      className={cn(
        headingClass,
        'text-neutral-N99 font-serif',
        classNames?.root
      )}
      data-tracker-component='heading'
      {...rest}
    >
      {children}
    </HeadingEl>
  );
}
